import { Heading, TableColumnType, TableComponent } from "@tutellus/tutellus-components"
import { useTranslation } from "react-i18next"
import { UserPoolStatsListSerialized } from "../../../domain/pool/Models/UserPoolStatsList"
import { UserPoolStatsSerialized } from "../../../domain/pool/Models/UserPoolStats"
import { useFormatAmounts } from "../../../hooks/useFormatAmounts"
import { TokenAmountCollapsibleTable } from "../TokenAmountCollapsibleTable"
import { TokenNameCell } from "../../TableCells/TokenNameCell"
import { useLayout } from "../../../context"

type WalletPoolStatsSimpleTableProps = { userPoolStatsList: UserPoolStatsListSerialized }

export const WalletPoolStatsSimpleTable = ({ userPoolStatsList }: WalletPoolStatsSimpleTableProps) => {
  const { t } = useTranslation("dashboard")
  const { currencyID } = useLayout()
  const { formatCurrencyAmounts, formatAmount } = useFormatAmounts()

  const columns: TableColumnType<UserPoolStatsSerialized>[] = [
    { key: "poolID", header: t("token"), content: ({ poolID }) => <TokenNameCell tokenID={poolID} /> },
    {
      key: "deposited.currencyAmounts",
      header: t("value"),
      content: ({ deposited }) => formatCurrencyAmounts(deposited.currencyAmounts),
      sortValue: ({ deposited }) => deposited.currencyAmounts?.find((c) => c.currencyID === currencyID)?.number || 0,
      align: "right",
    },
    {
      key: "rewards.pending.total",
      header: t("rewards"),
      content: ({ rewards }) => formatCurrencyAmounts(rewards.pending.total),
      sortValue: ({ rewards }) => rewards.pending.total.map((r) => r.number).reduce((acc, curr) => acc + curr, 0),
      align: "right",
    },
  ]

  const collapsibleContent = (row: UserPoolStatsSerialized) => {
    const totalDepositedAmount = row.depositedReserves.map((dr) => dr.number).reduce((acc, curr) => acc + curr, 0)
    const formattedTotalDepositedAmount = formatAmount(totalDepositedAmount)
    const depositedCaption = `${t("deposited")} ${formattedTotalDepositedAmount} LP`

    return (
      <>
        <TokenAmountCollapsibleTable data={row.depositedReserves} caption={depositedCaption} />
        <TokenAmountCollapsibleTable data={row.rewards.pending.tokens} caption={t("rewards")} />
      </>
    )
  }

  return (
    <>
      <Heading align="center" as="h2">
        {t("liquidity")}
      </Heading>
      <TableComponent
        columns={columns}
        data={userPoolStatsList?.items.filter((ups) => ups.deposited.number > 0)}
        defaultSort={{ key: "poolID", direction: "asc" }}
        collapsibleContent={(row) => collapsibleContent(row)}
        isRowCollapsible={() => true}
      />
    </>
  )
}
